import { Component, HostBinding } from '@angular/core';
import { Store } from '@ngrx/store';
import { FlowSandbox } from 'app/core/sandboxes/flow.sandbox';
import { FilterValues } from 'app/core/interfaces/filtervalues.interface';
import { EditFlowDetail } from 'app/core/store/edit-flow-detail/flow-detail.interface';
import { SessionState } from 'app/core/store/session/session.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { fadeInAnimation } from '../../../animations';
import * as fromRoot from '../../../core/store';
import { RootState } from '../../../core/store';
import { Florder } from '../../../core/interfaces/florder.interface';
import { Florders } from '../../../core/interfaces/florders.interface';
import { FlowEdit } from '../../../core/store/flows/flow-edit.interface';
import * as actions from '../../../core/store/flows/flows.actions';
import { FlowsClearFilterAction } from '../../../core/store/flows/flows.actions';
import { util } from '../../../core/util/util';
import { FlorderPageContainer } from '../../florders/florder-page-container/florder-page.container';
import { goAction } from '../../../core/store/router/router.actions';
import { distinctUntilChanged, map, takeUntil, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-flow-page',
  templateUrl: './flow-page.container.html',
  styleUrls: ['../../florders/florder-page-container/florder-page.container.scss'],
  animations: [fadeInAnimation],
  // host: { '[@fadeInAnimation]': '' }
  //  animations: [slideInDownAnimation]
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowPageContainer extends FlorderPageContainer {
  flows: Florders;
  loading: boolean = false;
  handshakingOrConfirming: boolean = false;
  customers: Customer[] = null;
  filterValues: FilterValues;
  isSuperUser: boolean;
  showCreate = false;

  @HostBinding('@fadeInAnimation') fadeInAnimation = true;

  constructor(private sandbox: FlowSandbox) {
    super();
    this.store
      .select('flows')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((flows: Florders) => {
        this.flows = flows;
        this.filter = util.deepCopy(flows.filter); // We will change this object, so we need a clone
        this.totalItems = flows.totalItems;
        this.ordersAreLoading = flows.loading;
        this.isDownloading = flows.downloading;
        this.handshakingOrConfirming = flows.handshaking;
        this.filterValues = flows.filterValues;
      });
    this.store.pipe(takeUntil(this.destroyed$)).subscribe((state: RootState) => {
      const authorization = state.session.authorization.data;
      const contractInfo = state.contractInfo;
      this.showCreate = !!(authorization.ORDER.CREATE && contractInfo.orderTypes.length);
    });
    this.store
      .select('editFlowDetail')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((editFlowDetail: EditFlowDetail) => {
        this.loading = editFlowDetail && editFlowDetail.mode && editFlowDetail.mode.loading;
      });
    this.store
      .pipe(
        map(state => state.contractInfo.failed),
        takeWhile(() => !this.destroyed),
        distinctUntilChanged()
      )
      .subscribe(failed => {
        if (failed) {
          this.loading = false;
        }
      });
    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.customerInfo),
        distinctUntilChanged()
      )
      .subscribe(customerInfo => {
        this.shipTos = (customerInfo.customer && customerInfo.customer.shipTos) || [];
      });
    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.session),
        distinctUntilChanged()
      )
      .subscribe((session: SessionState) => {
        this.isSuperUser = session.userContext.isSuperUser;
        if (this.isSuperUser) {
          this.customers = session.customerPermissions.data.queryOrGetFlow;
        } else {
          this.customers = null; // if not null, customer is part of filter form
        }
      });
  }

  protected reload(): void {
    this.store.dispatch(new actions.FlowsLoadAction({ filter: this.filter }));
  }

  createFlorder(): void {
    if (this.authorization.FLOW.CREATE) {
      this.store.dispatch(
        goAction({
          path: ['/flows/new'],
          query: { back: 'flows' },
        })
      );
    }
  }

  async openFlow(florder: Florder) {
    if (this.authorization.FLOW.GET) {
      let params;
      if (florder.salesOrderNumber) {
        params = { salesOrderNumber: florder.salesOrderNumber, back: 'flows' };
      } else {
        params = { back: 'flows' };
      }
      if (this.isSuperUser) {
        const customer = this.customers.find(c => c.id === florder.customerNumber);
        await this.sandbox.activateCustomerForEdit(customer);
      }
      this.store.dispatch(
        goAction({
          path: [`/flows/${florder.etmOrderNumber}`],
          query: params,
        })
      );
    }
  }

  /**
   * Accept the flow, 1 button click.  Use the filled in quantities
   * @param {Florder} flow
   */
  acceptFlow(flow: Florder) {
    this.sandbox.acceptFlow(flow);
  }

  cancelFlow(flow: Florder) {
    this.sandbox.cancelFlow(flow);
  }

  downloadList(exportType: string): void {
    if (!this.isDownloading) {
      const customerIds = this.isSuperUser ? this.customers.map(c => c.id) : [];
      this.store.dispatch(
        new actions.FlowsDownloadAction({
          exportType,
          filter: this.filter,
          totalItems: this.totalItems,
          customerIds,
        })
      );
    }
  }

  downloadDocument(flow: Florder) {
    const customerIds = this.isSuperUser ? this.customers.map(c => c.id) : [];
    this.sandbox.downloadDocument(flow, customerIds);
  }

  copyFlow(flow: Florder): void {
    this.sandbox.copyFlow(flow.customerNumber, flow.etmOrderNumber);
  }

  editFlow(flowData: { flow: Florder; isRegistrar: boolean; isHandshaker: boolean }): void {
    this.loading = true;
    this.sandbox.gotoEditFlow(flowData);
  }

  editDispute(flowData: { flow: Florder; isRegistrar: boolean; isHandshaker: boolean }): void {
    this.loading = true;
    this.sandbox.gotoEditDispute(flowData);
  }

  /**
   * Go to edit flow in 'accept' or 'handshake' modus
   * @param {Florder} flow
   */
  editAcceptFlow(flow: Florder): void {
    this.loading = true;
    this.sandbox.gotoAcceptFlow(flow);
  }

  validateEditFields(flowEdits) {
    let isValid: boolean = true;
    const invalidFlows: String[] = [];
    let localIsInvalid: boolean = true;
    flowEdits.map(flow => {
      if (flow.checked) {
        flow.items.map(item => {
          localIsInvalid = !isNaN(item.quantity) && item.quantity !== '' ? true : false;
        });
        if (!localIsInvalid) {
          invalidFlows.push(flow.etmOrderNumber);
        }
        isValid = localIsInvalid;
      }
    });
    return {
      invalidFlows,
      valid: isValid,
    };
  }

  acceptFlows(flowEditArray: FlowEdit[]) {
    const flows = flowEditArray
      .filter(flowEdit => flowEdit.checked)
      .map(flowEdit => {
        const flow: Florder = {
          ...flowEdit.flow,
          flowLineItems: flowEdit.flow.flowLineItems.map(item => {
            const edited = flowEdit.items.find(i => i.itemNumber === item.itemNumber);
            return {
              ...item,
              originalQuantity: edited.originalQuantity,
              definitiveQuantity: edited.definitiveQuantity,
            };
          }),
        };
        return flow;
      });

    // send rest call with delays
    const doRestCall = () => {
      this.sandbox.acceptFlowBatch(flows.pop());
      if (flows.length) {
        window.setTimeout(doRestCall, 100);
      }
    };

    doRestCall();
  }

  resetFilter() {
    this.store.dispatch(new FlowsClearFilterAction());
  }
}
